import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type JSX,
  type ReactNode,
} from 'react';
import { Listbox as HeadlessListbox, ListboxButton, ListboxOptions, Transition } from '@headlessui/react';
import { createPortal } from 'react-dom';

export interface ListboxProps<T> {
  buttonElement: JSX.Element;
  buttonAriaLabel?: string;
  buttonClassName?: (open?: boolean) => string;
  optionsClassName?: string;
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
  dataFfid?: string;
  inPortal?: boolean;
  portalTargetId?: string;
  children: ReactNode;
}

const MAX_HEIGHT = 300;

// A component is needed here because hooks cannot run inside Headless UI's render-prop callback.
// So we render a PortalPositionUpdater component whenever the portal is open, and it will call 
// updatePosition whenever the portal is rendered.
const PortalPositionUpdater = ({ updatePosition }: { updatePosition: () => void }) => {
  useLayoutEffect(() => {
    updatePosition();
  }, [updatePosition]);

  return null;
};

const Listbox = <T,>({
  buttonElement,
  buttonAriaLabel,
  buttonClassName,
  optionsClassName,
  value,
  onChange,
  disabled = false,
  children,
  dataFfid,
  inPortal = false,
  portalTargetId = 'react-modal-custom-portal',
}: ListboxProps<T>) => {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const escapeClosesOptionsRef = useRef(false);
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const [portalPosition, setPortalPosition] = useState<CSSProperties>({});
  const [maxHeight, setMaxHeight] = useState<number>(MAX_HEIGHT);
  const [isKeyboardNavigation, setIsKeyboardNavigation] = useState(false);

  const updatePortalPosition = useCallback(() => {
    if (!inPortal || !buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const verticalMargin = 16;
    setPortalPosition({ position: 'fixed', top: rect.bottom, left: rect.left, zIndex: 1000 });
    // The max height is 300 pixels, but we might have to clamp it lower than that if we're in a portal
    // (ie this is showing in a modal that can't scroll), and it is overflowing the bottom of the window
    const maxPortalHeight = Math.max(0, window.innerHeight - rect.bottom - verticalMargin);
    setMaxHeight(Math.min(maxPortalHeight, MAX_HEIGHT));
  }, [inPortal]);

  const handleKeyboardNavigation = useCallback(() => {
    setIsKeyboardNavigation(true);
  }, []);

  const handleButtonKeyUp = useCallback((event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Escape' && escapeClosesOptionsRef.current) {
      // Stop propagation if the escape happened when were in the listbox
      // This prevents the escape key from closing any parent modal
      event.stopPropagation();
      escapeClosesOptionsRef.current = false;
    }
  }, []);

  const handleOptionsKeyDown = useCallback((event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') {
      escapeClosesOptionsRef.current = true;
    }

    handleKeyboardNavigation();
  }, [handleKeyboardNavigation]);

  const handleOptionsPointerInteraction = useCallback(() => {
    setIsKeyboardNavigation(false);
  }, []);

  const stopPortalOptionEventPropagation = useCallback((event: React.SyntheticEvent) => {
    if (inPortal) {
      event.stopPropagation();
    }
  }, [inPortal]);

  useEffect(() => {
    if (!inPortal || typeof document === 'undefined') return;
    setPortalTarget(document.getElementById(portalTargetId) ?? document.body);
  }, [inPortal, portalTargetId]);

  useEffect(() => {
    if (!inPortal) return;

    window.addEventListener('resize', updatePortalPosition);
    document.addEventListener('scroll', updatePortalPosition, true);

    return () => {
      window.removeEventListener('resize', updatePortalPosition);
      document.removeEventListener('scroll', updatePortalPosition, true);
    };
  }, [inPortal, updatePortalPosition]);

  useEffect(() => {
    if (!isKeyboardNavigation) return;

    document.addEventListener('mousemove', handleOptionsPointerInteraction);

    return () => {
      document.removeEventListener('mousemove', handleOptionsPointerInteraction);
    };
  }, [isKeyboardNavigation, handleOptionsPointerInteraction]);

  const options = (
    <Transition
      enter="transition ease-out duration-100"
      enterFrom="transform origin-top scale-y-0"
      enterTo="transform origin-top scale-y-100"
      leave="transition ease-in duration-100"
      leaveFrom="transform origin-top scale-y-150"
      leaveTo="transform origin-top scale-y-0"
    >
      <ListboxOptions
        modal={false}
        style={{ maxHeight: maxHeight, ...(inPortal ? portalPosition : {}) }}
        className={`${optionsClassName ?? 'absolute left-0 top-40 w-full'} cursor-pointer outline-none focus:outline-none focus-visible:outline-none ${isKeyboardNavigation ? '[&_[data-focus]]:outline [&_[data-focus]]:outline-2 [&_[data-focus]]:outline-black' : ''}`}
        onKeyDown={handleOptionsKeyDown}
        onTouchStart={handleOptionsPointerInteraction}
        onMouseUp={stopPortalOptionEventPropagation}
        onClick={stopPortalOptionEventPropagation}
        onTouchEnd={stopPortalOptionEventPropagation}
      >
        {children}
      </ListboxOptions>
    </Transition>
  );

  return (
    <HeadlessListbox value={value} onChange={onChange}>
      {({ open }) => (
        <>
          <ListboxButton
            ref={buttonRef}
            onKeyDown={handleKeyboardNavigation}
            onKeyUp={handleButtonKeyUp}
            disabled={disabled}
            data-ffid={dataFfid}
            className={buttonClassName?.(open) ?? 'flex items-center justify-between'}
            aria-label={buttonAriaLabel}
          >
            {buttonElement}
          </ListboxButton>

          {inPortal && open && <PortalPositionUpdater updatePosition={updatePortalPosition} />}
          {inPortal && portalTarget ? createPortal(options, portalTarget) : options}
        </>
      )}
    </HeadlessListbox>
  );
};

export default Listbox;